/**
 * voice-enrollment Web のルートコンポーネント。
 *
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business 声紋登録 Web の画面遷移ステートマシン。consent → language → recording → result の流れを管理し、API client と locale bundle を子コンポーネントに渡す
 * @graph-connects createApiClient [calls] API クライアント生成
 * @graph-connects getLocaleBundle [calls] 言語別の文言と読み上げスクリプト取得
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Layout } from './components/Layout.js';
import type { StepperStep } from './components/Stepper.js';
import { ConsentPage } from './pages/ConsentPage.js';
import { LanguageSelectPage } from './pages/LanguageSelectPage.js';
import { RecordingPage } from './pages/RecordingPage.js';
import { ResultPage } from './pages/ResultPage.js';
import { getLocaleBundle, type LanguageCode } from './i18n/index.js';
import { CONSENT_VERSION, type AppState, type CompletedRecording, type FlowStep } from './types.js';
import { createApiClient, type ApiClient } from './api.js';

/** @graph-connects none */
const INITIAL_STATE: AppState = {
  step: 'consent',
  language: 'ja',
  consentAccepted: false,
  recordings: [],
  failureReason: null,
};

interface AppProps {
  /** テスト用 — API クライアント差し替え */
  apiClient?: ApiClient;
  /** API base URL（main.tsx で VITE_API_URL を読んで渡す） */
  apiBaseUrl?: string;
}

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business voice-enrollment Web のルートコンポーネント。画面遷移と enrollment submit を統括する
 * @graph-connects createApiClient [calls] API クライアント生成
 * @graph-connects getLocaleBundle [calls] 言語別 bundle 取得
 */
export function App({ apiClient, apiBaseUrl }: AppProps = {}): ReactNode {
  const [state, setState] = useState<AppState>(INITIAL_STATE);
  const [submitting, setSubmitting] = useState(false);
  const client = apiClient ?? createApiClient({ baseUrl: apiBaseUrl });
  const bundle = getLocaleBundle(state.language);
  const { messages, readingScripts } = bundle;

  const goTo = useCallback((step: FlowStep) => {
    setState((s) => ({ ...s, step }));
  }, []);

  const handleConsentAccept = useCallback(() => {
    setState((s) => ({ ...s, consentAccepted: true, step: 'language' }));
  }, []);

  const handleLanguageConfirm = useCallback((language: LanguageCode) => {
    setState((s) => ({ ...s, language, step: 'recording' }));
  }, []);

  const handleRecordingsComplete = useCallback(
    async (recordings: CompletedRecording[]) => {
      setSubmitting(true);
      try {
        await client.submitEnrollment({
          language: state.language,
          consentVersion: CONSENT_VERSION,
          recordings,
        });
        setState((s) => ({
          ...s,
          recordings,
          step: 'success',
          failureReason: null,
        }));
      } catch (error) {
        setState((s) => ({
          ...s,
          recordings,
          step: 'failure',
          failureReason: error instanceof Error ? error.message : 'Unknown error',
        }));
      } finally {
        setSubmitting(false);
      }
    },
    [client, state.language],
  );

  const handleRestart = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  const steps = useMemo<StepperStep[]>(
    () => [
      { id: 'consent', label: messages.consent.title },
      { id: 'language', label: messages.languageSelect.title },
      { id: 'recording', label: messages.recording.title },
      { id: 'result', label: messages.result.successTitle },
    ],
    [messages],
  );

  const stepIndex = stepIndexFor(state.step);

  return (
    <div aria-busy={submitting}>
      <Layout appTitle={messages.appTitle} steps={steps} currentStepIndex={stepIndex}>
        {state.step === 'consent' ? (
          <ConsentPage
            messages={messages}
            onAccept={handleConsentAccept}
            onDecline={() => {
              goTo('failure');
            }}
          />
        ) : null}
        {state.step === 'language' ? (
          <LanguageSelectPage
            messages={messages}
            initialLanguage={state.language}
            onConfirm={handleLanguageConfirm}
          />
        ) : null}
        {state.step === 'recording' ? (
          <RecordingPage
            messages={messages}
            scripts={readingScripts}
            onComplete={(recordings) => {
              void handleRecordingsComplete(recordings);
            }}
          />
        ) : null}
        {state.step === 'success' ? (
          <ResultPage messages={messages} variant="success" onRestart={handleRestart} />
        ) : null}
        {state.step === 'failure' ? (
          <ResultPage messages={messages} variant="failure" onRestart={handleRestart} />
        ) : null}
      </Layout>
    </div>
  );
}

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business フローの step を stepper の index に変換する純関数
 * @graph-connects none
 */
function stepIndexFor(step: FlowStep): number {
  switch (step) {
    case 'consent':
      return 0;
    case 'language':
      return 1;
    case 'recording':
      return 2;
    case 'success':
    case 'failure':
      return 3;
  }
}
